import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiMail,
  FiMapPin,
} from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  orderNumber: "",
  message: "",
};

const subjectOptions = [
  "Product or pairing advice",
  "Order and delivery question",
  "Gift order help",
  "Return or quality concern",
  "Press or partnership",
  "Something else",
];

const quickAnswers = [
  {
    title: "Where is my order?",
    text: "Tracking details appear after a parcel leaves our pantry. Review dispatch timing and chilled-delivery notes.",
    label: "Shipping guide",
    to: "/shipping",
  },
  {
    title: "Something arrived wrong?",
    text: "Keep the packaging and take a clear photo. Our returns guide explains what to include so we can help quickly.",
    label: "Returns guide",
    to: "/returns",
  },
  {
    title: "Building a great board?",
    text: "Tell us the occasion, guest count, and flavors you love. We will point you toward a balanced spread.",
    label: "Browse the pantry",
    to: "/shop",
  },
];

function FieldError({ id, children }) {
  if (!children) return null;
  return (
    <span className="contact-page__error" id={id} role="alert">
      {children}
    </span>
  );
}

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const submitTimerRef = useRef(null);
  const statusRef = useRef(null);

  useEffect(
    () => () => {
      if (submitTimerRef.current) window.clearTimeout(submitTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (showSuccess) statusRef.current?.focus();
  }, [showSuccess]);

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setShowSuccess(false);
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) nextErrors.firstName = "Enter your first name.";
    if (!formData.lastName.trim()) nextErrors.lastName = "Enter your last name.";

    if (!formData.email.trim()) {
      nextErrors.email = "Enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.subject) nextErrors.subject = "Choose a subject.";

    if (!formData.message.trim()) {
      nextErrors.message = "Tell us how we can help.";
    } else if (formData.message.trim().length < 20) {
      nextErrors.message = "Please add a little more detail (at least 20 characters).";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      const firstInvalidField = document.getElementById(Object.keys(nextErrors)[0]);
      firstInvalidField?.focus();
      return;
    }

    setErrors({});
    setShowSuccess(false);
    setIsSubmitting(true);

    submitTimerRef.current = window.setTimeout(() => {
      setFormData(initialFormData);
      setIsSubmitting(false);
      setShowSuccess(true);
      submitTimerRef.current = null;
    }, 900);
  };

  return (
    <main className="contact-page">
      <section className="contact-page__hero">
        <div className="contact-page__hero-copy">
          <p className="contact-page__eyebrow">Customer care</p>
          <h1>Let&apos;s talk food.</h1>
          <p>
            Questions about an order, a cheese, or the right gift? The
            {` ${siteConfig.brandName} `}team is here to point you in a delicious
            direction.
          </p>
        </div>
        <div className="contact-page__response-note">
          <FiClock aria-hidden="true" />
          <div>
            <span>Typical reply</span>
            <strong>Within 1–2 business days</strong>
            <small>{siteConfig.contact.hours}</small>
          </div>
        </div>
      </section>

      <section className="contact-page__channels" aria-label="Contact options">
        <a className="contact-page__channel" href={`mailto:${siteConfig.contact.email}`}>
          <FiMail aria-hidden="true" />
          <span>Email us</span>
          <strong>{siteConfig.contact.email}</strong>
        </a>
        <div className="contact-page__channel">
          <FiMapPin aria-hidden="true" />
          <span>Find us</span>
          <strong>{siteConfig.contact.address}</strong>
        </div>
      </section>

      <section className="contact-page__workbench">
        <aside className="contact-page__aside">
          <p className="contact-page__eyebrow">Before you write</p>
          <h2>A few details help us help you faster.</h2>
          <ul>
            <li>
              <span>01</span>
              Include your order number if your question is about a purchase.
            </li>
            <li>
              <span>02</span>
              For damaged or incorrect items, describe the packaging and keep a
              clear photo ready.
            </li>
            <li>
              <span>03</span>
              For pairing advice, share the occasion, guest count, and any dietary
              needs.
            </li>
          </ul>
          <div className="contact-page__aside-links">
            <Link to="/shipping">Read shipping details</Link>
            <Link to="/returns">Review return policy</Link>
          </div>
        </aside>

        <div className="contact-page__form-wrap">
          <div className="contact-page__form-heading">
            <p className="contact-page__eyebrow">Send a note</p>
            <h2>How can we help?</h2>
            <p>Fields marked with an asterisk are required.</p>
          </div>

          {showSuccess && (
            <div
              className="contact-page__success"
              ref={statusRef}
              role="status"
              tabIndex="-1"
            >
              <FiCheckCircle aria-hidden="true" />
              <div>
                <strong>Thanks—your message is ready.</strong>
                <p>
                  For the fastest response, email the same details to {siteConfig.contact.email}.
                </p>
              </div>
            </div>
          )}

          <form
            aria-busy={isSubmitting}
            className="contact-page__form"
            noValidate
            onSubmit={handleSubmit}
          >
            <div className="contact-page__field-grid">
              <div className="contact-page__field">
                <label htmlFor="firstName">First name *</label>
                <input
                  aria-describedby={errors.firstName ? "firstName-error" : undefined}
                  aria-invalid={Boolean(errors.firstName)}
                  autoComplete="given-name"
                  disabled={isSubmitting}
                  id="firstName"
                  name="firstName"
                  onChange={handleInputChange}
                  type="text"
                  value={formData.firstName}
                />
                <FieldError id="firstName-error">{errors.firstName}</FieldError>
              </div>

              <div className="contact-page__field">
                <label htmlFor="lastName">Last name *</label>
                <input
                  aria-describedby={errors.lastName ? "lastName-error" : undefined}
                  aria-invalid={Boolean(errors.lastName)}
                  autoComplete="family-name"
                  disabled={isSubmitting}
                  id="lastName"
                  name="lastName"
                  onChange={handleInputChange}
                  type="text"
                  value={formData.lastName}
                />
                <FieldError id="lastName-error">{errors.lastName}</FieldError>
              </div>

              <div className="contact-page__field">
                <label htmlFor="email">Email *</label>
                <input
                  aria-describedby={errors.email ? "email-error" : undefined}
                  aria-invalid={Boolean(errors.email)}
                  autoComplete="email"
                  disabled={isSubmitting}
                  id="email"
                  inputMode="email"
                  name="email"
                  onChange={handleInputChange}
                  type="email"
                  value={formData.email}
                />
                <FieldError id="email-error">{errors.email}</FieldError>
              </div>

              <div className="contact-page__field">
                <label htmlFor="subject">Subject *</label>
                <select
                  aria-describedby={errors.subject ? "subject-error" : undefined}
                  aria-invalid={Boolean(errors.subject)}
                  disabled={isSubmitting}
                  id="subject"
                  name="subject"
                  onChange={handleInputChange}
                  value={formData.subject}
                >
                  <option value="">Choose one</option>
                  {subjectOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <FieldError id="subject-error">{errors.subject}</FieldError>
              </div>

              <div className="contact-page__field">
                <label htmlFor="orderNumber">Order number <span>(optional)</span></label>
                <input
                  autoComplete="off"
                  disabled={isSubmitting}
                  id="orderNumber"
                  name="orderNumber"
                  onChange={handleInputChange}
                  placeholder="e.g. 1048-256"
                  type="text"
                  value={formData.orderNumber}
                />
              </div>
            </div>

            <div className="contact-page__field contact-page__field--wide">
              <label htmlFor="message">Message *</label>
              <textarea
                aria-describedby={errors.message ? "message-error" : "message-hint"}
                aria-invalid={Boolean(errors.message)}
                disabled={isSubmitting}
                id="message"
                maxLength="1200"
                name="message"
                onChange={handleInputChange}
                rows="7"
                value={formData.message}
              />
              <div className="contact-page__field-footer">
                <FieldError id="message-error">{errors.message}</FieldError>
                {!errors.message && (
                  <span id="message-hint">Please do not include payment details.</span>
                )}
                <span>{formData.message.length}/1200</span>
              </div>
            </div>

            <div className="contact-page__submit-row">
              <button disabled={isSubmitting} type="submit">
                {isSubmitting ? (
                  <>
                    <span className="contact-page__spinner" aria-hidden="true" />
                    Checking request…
                  </>
                ) : (
                  <>
                    Send message <FiArrowRight aria-hidden="true" />
                  </>
                )}
              </button>
              <p>
                By submitting, you acknowledge our <Link to="/privacy">privacy policy</Link>.
              </p>
            </div>
          </form>
        </div>
      </section>

      <section className="contact-page__quick" aria-labelledby="quick-answers-title">
        <div className="contact-page__quick-heading">
          <p className="contact-page__eyebrow">Quick answers</p>
          <h2 id="quick-answers-title">You may not need to wait.</h2>
        </div>
        <div className="contact-page__quick-grid">
          {quickAnswers.map((item) => (
            <article key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <Link to={item.to}>
                {item.label} <FiArrowRight aria-hidden="true" />
              </Link>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

export default Contact;
